import { type ComponentProps, forwardRef, useEffect, useRef, useState } from "react";

import { setRefs } from "../utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface DatePickerProps extends Omit<ComponentProps<typeof Button>, "value" | "onChange"> {
  value?: Date | string;
  onChange?: (value?: Date) => void;
  calendarProps?: Omit<ComponentProps<typeof Calendar>, "mode">;
  closeOnSelect?: boolean;
  readOnly?: boolean;
  /** Controlled open state. When provided, the popover is controlled externally. */
  open?: boolean;
  /** Called when the popover open state changes. Works in both controlled and uncontrolled mode. */
  onOpenChange?: (open: boolean) => void;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      disabled,
      value,
      onChange,
      onBlur,
      calendarProps,
      closeOnSelect = false,
      children,
      readOnly,
      open: controlledOpen,
      onOpenChange,
      ...props
    },
    ref
  ) => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(false);
    const [date, setDate] = useState<Date | undefined>(parseDatePickerValue(value));

    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    const setIsOpen = (open: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(open);
      }
      onOpenChange?.(open);
    };

    const handleSelect = (date?: Date) => {
      setDate(date);
      onChange?.(date);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      closeOnSelect && setIsOpen(false);
    };

    const handleBlur = (event: any) => {
      setTimeout(() => {
        // Don't trigger blur If the focus is still within the field or dropdown.
        if (
          buttonRef.current?.contains(document.activeElement) ||
          dropdownRef.current?.contains(document.activeElement)
        ) {
          return;
        }

        onBlur?.(event);
      }, 0);
    };

    useEffect(() => {
      setDate(parseDatePickerValue(value));
    }, [value]);

    return (
      <Popover modal open={isOpen} onOpenChange={(open) => !disabled && !readOnly && setIsOpen(open)}>
        <PopoverTrigger onBlur={handleBlur} asChild>
          <Button disabled={disabled || readOnly} ref={setRefs(ref, buttonRef)} {...props}>
            {children}
          </Button>
        </PopoverTrigger>

        <PopoverContent ref={dropdownRef} onBlur={handleBlur} className="w-auto p-0">
          <Calendar
            mode="single"
            initialFocus
            {...calendarProps}
            selected={date}
            defaultMonth={date || undefined}
            onSelect={handleSelect}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
DatePicker.displayName = "DatePicker";

export function parseDatePickerValue(value?: Date | string) {
  return value ? (typeof value === "string" ? new Date(value) : value) : undefined;
}
